// Ingest contract (13 §Shapes `IngestItem`, §Ingest contract). The key determines the actor; the
// body never carries one. Objects are strict: an unknown field is a validation error, and any
// `actor`/`actor_*` field is reported as `actor_not_allowed` by `findActorFields`.
import { z } from "zod";
import {
  AggregateGrain,
  ApprovalStatus,
  Currency,
  IngestCostKind,
  IngestReceiptStatus,
  TelemetryLevel,
  TimeEntryKind,
  Visibility,
} from "./enums";
import { boundedRecord, DottedName, IsoDate, Timestamp, TimeZone, Uuid } from "./primitives";

export const INGEST_LIMITS = {
  maxItems: 500,
  maxBodyBytes: 1_048_576,
  maxItemKeyLength: 128,
  maxAttrs: 32,
  maxAttrValueLength: 512,
  futureSkewMs: 5 * 60_000,
  maxAgeMs: 90 * 86_400_000,
} as const;

const AttrValue = z.union([
  z.string().max(INGEST_LIMITS.maxAttrValueLength),
  z.number().finite(),
  z.boolean(),
]);
export const IngestAttrs = z
  .record(z.string().max(64), AttrValue)
  .meta({ maxProperties: INGEST_LIMITS.maxAttrs })
  .refine((a) => Object.keys(a).length <= INGEST_LIMITS.maxAttrs, {
    message: `at most ${INGEST_LIMITS.maxAttrs} attrs`,
  });

/** External, opaque subject (candidate, ticket). */
export const ExternalSubject = z.strictObject({
  kind: z.string().min(1).max(64),
  id: z.string().min(1).max(256),
});

/** `{system}:{kind}:{external_id}`, e.g. `litellm:cos-acme-1a2b:req_123` (fix #18). */
export const MeterIdentity = z
  .string()
  .max(256)
  .regex(/^[a-z][a-z0-9_]*:[^:\s]+:\S+$/, "meter identity {system}:{kind}:{external_id}");

/**
 * Meter-identity systems the platform posts under itself (S1-11 review #14), the one policy for
 * every writer: manual and CSV postings may not use them, and an ingest key may not bind or claim
 * them — except `litellm:{its DE's key alias}`, which the server assigns:
 *   `sub:`        DE subscription months (`sub:{subscription}:{YYYY-MM}`);
 *   `time_entry:` human-time cost of a time entry;
 *   `reversal:`   reversals and credit compensations (`reversal:cost_event:{id}`);
 *   `litellm:`    LiteLLM usage, one namespace per DE key alias;
 *   `run_step:`   agent tokens and tool calls of a run step (M3-04, the one charge basis);
 *   `sandbox:`    sandbox minutes of a run (M3-04, from `sandbox.stopped`).
 */
export const RESERVED_METER_SYSTEMS = [
  "sub",
  "time_entry",
  "reversal",
  "litellm",
  "run_step",
  "sandbox",
  // DC-10: a drift audit's eval runs, posted by `job:drift_audit.run`.
  "drift_audit",
  // AGENT-PLUMBING: paid services an agent reported (`runs.reportCost`, cost/agent-reported.ts).
  "agent_reported",
  // SKILLSMITH-HUB: a system agent's model turns, the company's "System" cost line.
  "system_agent",
  // B5-DECIDE: a classifier call (`decide`, the board-event gate), on the DE's seat.
  "decision",
] as const;

/** Whether a meter identity or namespace lies under a platform-reserved system. */
export const isReservedMeter = (identityOrNamespace: string) =>
  RESERVED_METER_SYSTEMS.some((s) => identityOrNamespace.startsWith(`${s}:`));

/**
 * A meter namespace `{system}:{scope}` bound to an ingest key (S1-08 review #1): the key may claim
 * only meter identities `{namespace}:…` — plus `litellm:{its DE's key alias}:…`, which is always
 * its own. Reserved systems (`RESERVED_METER_SYSTEMS`) are server-owned and never bound by hand.
 */
export const MeterNamespace = z
  .string()
  .max(128)
  .regex(/^[a-z][a-z0-9_]*:[^:\s,]+$/, "meter namespace {system}:{scope}")
  .refine((ns) => !isReservedMeter(ns), {
    message: `namespaces under ${RESERVED_METER_SYSTEMS.map((s) => `${s}:`).join(", ")} are server-owned`,
  });

/** Why a `rejected` receipt was refused (the item itself; the batch goes on). */
export const IngestRejection = z.enum([
  "meter_namespace",
  // MONITORING-P1: a telemetry item naming a run that is not one of the key's DE's runs.
  "run_not_owned",
]);

/** `cost_event.meter` (13 §cost_event). */
export const CostMeter = z
  .strictObject({
    model: z.string().max(128).optional(),
    tokens_in: z.int().min(0).optional(),
    tokens_out: z.int().min(0).optional(),
    cache_read: z.int().min(0).optional(),
    minutes: z.number().min(0).optional(),
    calls: z.int().min(0).optional(),
    /** AGENT-PLUMBING: images a model call generated, where the gateway reports a count. */
    images: z.int().min(0).optional(),
    /**
     * WALLET-P1: a unit-priced use — a gateway media call (`video_minute`, `audio_minute`,
     * `tts_1k_chars`, `web_search`, `web_fetch`) or an agent-reported spend's category — and how many of it.
     */
    unit: z
      .string()
      .max(40)
      .regex(/^[a-z][a-z0-9_]{1,39}$/)
      .optional(),
    quantity: z.number().min(0).max(1_000_000_000).optional(),
    /** WALLET-P1: `quantity` is counted in 1/divisor of the unit (seconds of a minute: 60). */
    quantity_divisor: z.int().min(1).max(1_000_000).optional(),
    /** WALLET-P1: a search or fetch call that did not succeed: never billable to the client. */
    failed_tool: z.literal(true).optional(),
    /**
     * WALLET-P1: the run a covered gateway call belonged to (booked at zero, its run's steps
     * paid for it); honoured only for a run of the row's holder.
     */
    run_id: Uuid.optional(),
    /**
     * AGENT-PLUMBING: a tagged gateway row's own figure (micros); the row's amount is the part
     * its run's steps had not already charged (sources/litellm/tagged.ts).
     */
    gateway_micros: z.int().min(0).optional(),
    /** DE-QUICK-ACK: what a run's model call was for (`quick_reply`: the instant first reply). */
    tag: z.enum(["quick_reply"]).optional(),
    /**
     * MODEL-KEYS-PLANS: a run step's call on the company's own provider key (set by the API's
     * step consumer; honoured on `run_step:` rows only): recorded, never billed to the wallet.
     */
    model_source: z.literal("own").optional(),
    /**
     * BILLING-LEDGER: a run's cost that is the company's system usage (its Skillsmith's
     * maintenance; set by the API's step consumer): on no DE's cost lines.
     */
    usage_kind: z.literal("system_maintenance").optional(),
    /**
     * VOICE-CHOICES: a voice session segment's voice and price, written by the API's own
     * `voice.usage.record` only (honoured on `source_ref {system: voice, kind: segment}` rows,
     * which no ingest item can make): the wallet charges `billed_micros`, named after the voice.
     */
    voice_id: z.string().max(40).optional(),
    voice_name: z.string().max(60).optional(),
    voice_tier: z.enum(["free", "self_hosted", "premium"]).optional(),
    voice_engine: z.string().max(40).optional(),
    rate_micros_per_minute: z.int().min(0).optional(),
    billed_micros: z.int().min(0).optional(),
  })
  .meta({ id: "CostMeter" });
export type CostMeter = z.infer<typeof CostMeter>;

export const IngestCost = z
  .strictObject({
    cost_kind: IngestCostKind,
    amount_micros: z.int().optional(),
    amount_minor: z.int().optional(),
    currency: Currency,
    meter_identity: MeterIdentity,
    meter: CostMeter.optional(),
    visibility: Visibility.optional(),
  })
  .refine((c) => c.amount_micros !== undefined || c.amount_minor !== undefined, {
    message: "amount_micros or amount_minor is required",
    path: ["amount_micros"],
  });

/** Day or week aggregate per person per seat (workpulse.md §10.2); never raw intervals. */
export const IngestTime = z.strictObject({
  source_event_id: z.string().min(1).max(256),
  work_date: IsoDate,
  grain: AggregateGrain,
  external_person_id: z.string().min(1).max(256),
  external_seat_id: z.string().min(1).max(256).optional(),
  external_client_id: z.string().min(1).max(256).optional(),
  /**
   * The aggregate's measured seconds. `0` voids the logical row (`source_event_id`): the source
   * no longer measures anything for it (WorkPulse: every interval of the day deleted).
   */
  seconds: z
    .int()
    .min(0)
    .max(31 * 86_400),
  kind: TimeEntryKind,
  approval_status: ApprovalStatus.optional(),
  stats: boundedRecord(z.number().finite(), 32).optional(),
});

const itemBase = {
  /** Unique per DE; the per-item idempotency key (`ingest_receipt`). */
  item_key: z.string().min(1).max(INGEST_LIMITS.maxItemKeyLength),
  /** Open but namespaced vocabulary (`task.*`, `output.*`, `candidate.*`, …). */
  type: DottedName,
  /** Must lie within [now − 90 d, now + 5 min] (`checkOccurredAt`). */
  occurred_at: Timestamp,
  quantity: z.number().finite().optional(),
  value: z.number().finite().optional(),
  subject: ExternalSubject.optional(),
  attrs: IngestAttrs.optional(),
};

export const IngestActivityItem = z.strictObject({ ...itemBase, kind: z.literal("activity") });
export const IngestOutcomeItem = z.strictObject({ ...itemBase, kind: z.literal("outcome") });
export const IngestCostItem = z.strictObject({
  ...itemBase,
  kind: z.literal("cost"),
  cost: IngestCost,
});
export const IngestTimeItem = z.strictObject({
  ...itemBase,
  kind: z.literal("time"),
  time: IngestTime,
});

/**
 * MONITORING-P1 (docs/design/2026-11-01-monitoring-in-companyos.md §2): what a DE's bridge saw,
 * and what the detached job wrote to its `events.ndjson`. Never model text, message bodies,
 * tool arguments or file contents: ids, names, numbers and one short line. String values are
 * redacted again server side (keys and tokens become `[redacted:<kind>]`).
 *
 * Event types CompanyOS acts on (any other dotted type is stored and shown on the timeline):
 *   run.wake_pushed, run.checked_out, run.completed          the run's life on the host
 *   step.tool        {tool, duration_ms, ok}                  a tool call (never its arguments;
 *                    + thread_id                              GUARDS-ARGS: a thread-reading tool's
 *                                                             target, the id only: thread guards;
 *                    + tool_arg                               a terminal-like tool's summary:
 *                                                             command names, URL hosts and paths,
 *                                                             no query, body or header: tool_arg
 *                                                             guards; sanitised again server side)
 *   outbox.posted / send_now.sent {thread_id, message_id}     a post (post-storm, thread guards)
 *   steer.acked, bridge.log                                   shown as is
 *   stop.acked       {stop_request_id, ok, method, latency_ms} the answer to a stop request
 *   job.started      {job_kind, slides_total}                 a job opens (`job_id` required
 *   job.stage_started {stage, attempt, budget_s}              on every job.* event)
 *   job.stage_ended  {stage, attempt, ok}
 *   job.slide_scored {slide, score, critic, pass}
 *   job.redo         {slide, reason}
 *   job.cost         {model, tokens_in, tokens_out, micros}
 *   job.delivered    {thread_id, files, post_ids}
 *   job.done         {status: ok | failed | cancelled}
 *   bridge.heartbeat {version, de_keys, telemetry_on, uptime_s, host?}  MONITORING-P2: the
 *                    bridge's own pulse (no run_id), e.g. every 5 minutes; the fleet page and
 *                    the `bridge_silent` alert read it
 */
export const TelemetryData = z.strictObject({
  tool: z.string().max(128).optional(),
  /** GUARDS-ARGS: `step.tool` of a terminal-like tool, e.g. `curl camofox:9377/tabs/x/evaluate`. */
  tool_arg: z.string().max(300).optional(),
  duration_ms: z.int().min(0).max(86_400_000).optional(),
  ok: z.boolean().optional(),
  thread_id: z.string().max(128).optional(),
  message_id: z.string().max(128).optional(),
  post_ids: z.array(z.string().max(128)).max(20).optional(),
  subject: z.string().max(200).optional(),
  stage: z.string().max(64).optional(),
  attempt: z.int().min(0).max(1000).optional(),
  budget_s: z.int().min(1).max(86_400).optional(),
  slide: z.int().min(0).max(1000).optional(),
  score: z.number().finite().min(-1000).max(1000).optional(),
  critic: z.string().max(64).optional(),
  pass: z.boolean().optional(),
  reason: z.string().max(300).optional(),
  model: z.string().max(128).optional(),
  tokens_in: z.int().min(0).optional(),
  tokens_out: z.int().min(0).optional(),
  micros: z.int().min(0).optional(),
  files: z.int().min(0).max(10_000).optional(),
  job_kind: z.string().max(64).optional(),
  slides_total: z.int().min(0).max(1000).optional(),
  status: z.enum(["ok", "failed", "cancelled"]).optional(),
  stop_request_id: Uuid.optional(),
  method: z.string().max(64).optional(),
  latency_ms: z.int().min(0).max(3_600_000).optional(),
  // MONITORING-P2: `bridge.heartbeat`.
  /** The bridge's version (`2.8.0`). */
  version: z.string().max(64).optional(),
  /** The DE keys (profile names) the bridge serves on its host; ids, never key material. */
  de_keys: z.array(z.string().min(1).max(128)).max(50).optional(),
  /** Whether the bridge ships run telemetry (a heartbeat still comes when it does not). */
  telemetry_on: z.boolean().optional(),
  /** Seconds since the bridge process started. */
  uptime_s: z.int().min(0).max(315_360_000).optional(),
  /** The host or satellite name (`copy-11`) as the bridge knows it. */
  host: z.string().min(1).max(120).optional(),
  /** Anything else the shipper adds (scalars, ≤ 16 keys); shown, never acted on. */
  extra: boundedRecord(
    z.union([z.string().max(300), z.number().finite(), z.boolean()]),
    16,
    z.string().max(64),
  ).optional(),
});
export type TelemetryData = z.infer<typeof TelemetryData>;

export const IngestTelemetry = z.strictObject({
  /** Who emitted it on the host (`bridge`, `job`); `seq` counts per source, gaps are visible. */
  source: z
    .string()
    .max(32)
    .regex(/^[a-z][a-z0-9_-]*$/),
  seq: z.int().min(0),
  /** The run it belongs to: one of this DE's runs (else the item is `rejected`). */
  run_id: Uuid.optional(),
  /** The detached job (every `job.*` event carries it). */
  job_id: z
    .string()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9._:-]+$/)
    .optional(),
  level: TelemetryLevel.optional(),
  /** One short line (a log line, a warning), ≤ 1,000 chars. */
  message: z.string().max(1000).optional(),
  /** Values the shipper already replaced with `[redacted:<kind>]`. */
  redactions: z.int().min(0).max(10_000).optional(),
  data: TelemetryData.optional(),
});

export const IngestTelemetryItem = z.strictObject({
  item_key: itemBase.item_key,
  type: itemBase.type,
  occurred_at: itemBase.occurred_at,
  kind: z.literal("telemetry"),
  telemetry: IngestTelemetry,
});

export const IngestItem = z
  .discriminatedUnion("kind", [
    IngestActivityItem,
    IngestOutcomeItem,
    IngestCostItem,
    IngestTimeItem,
    IngestTelemetryItem,
  ])
  .meta({ id: "IngestItem" });
export type IngestItem = z.infer<typeof IngestItem>;

export const IngestBatch = z
  .strictObject({
    /** Required when any item is `time`; must equal the company time zone (`tz_mismatch`). */
    tz: TimeZone.optional(),
    items: z.array(IngestItem).min(1).max(INGEST_LIMITS.maxItems),
  })
  .superRefine((b, ctx) => {
    if (b.tz === undefined && b.items.some((i) => i.kind === "time"))
      ctx.addIssue({ code: "custom", path: ["tz"], message: "tz is required for time items" });
    const seen = new Set<string>();
    b.items.forEach((item, i) => {
      if (seen.has(item.item_key))
        ctx.addIssue({ code: "custom", path: ["items", i, "item_key"], message: "duplicate" });
      seen.add(item.item_key);
    });
  })
  .meta({ id: "IngestBatch" });
export type IngestBatch = z.infer<typeof IngestBatch>;

export const IngestReceipt = z
  .object({
    item_key: z.string(),
    status: IngestReceiptStatus,
    event_id: Uuid.optional(),
    /** `rejected` only: why the item was refused. */
    reason: IngestRejection.optional(),
  })
  .meta({ id: "IngestReceipt" });

export const IngestResponse = z
  .object({ receipts: z.array(IngestReceipt) })
  .meta({ id: "IngestResponse" });
export type IngestResponse = z.infer<typeof IngestResponse>;

/** Scope each item kind needs (13 §Ingest contract rule 2). `time` has no DE-key scope. */
export const ingestScopeFor = {
  activity: "ingest:activity",
  outcome: "ingest:outcome",
  cost: "ingest:cost",
  telemetry: "ingest:telemetry",
  time: null,
} as const;

/**
 * Paths of every `actor` / `actor_*` key in a request body, at the batch or item level. A
 * non-empty result is `422 actor_not_allowed` (13 §Ingest contract rule 1).
 */
export function findActorFields(body: unknown): string[] {
  const found: string[] = [];
  const visit = (value: unknown, path: string) => {
    if (Array.isArray(value)) {
      for (const [i, v] of value.entries()) visit(v, `${path}${path ? "." : ""}${i}`);
    } else if (value !== null && typeof value === "object")
      for (const [k, v] of Object.entries(value)) {
        const p = `${path}${path ? "." : ""}${k}`;
        if (k === "actor" || k.startsWith("actor_")) found.push(p);
        else if (k !== "attrs") visit(v, p);
      }
  };
  visit(body, "");
  return found;
}

/** Clock-skew rule 7: future > 5 min or older than 90 days (unless backfill) is refused. */
export function checkOccurredAt(
  occurredAt: string,
  now: Date,
  opts: { backfill?: boolean } = {},
): "ok" | "too_far_in_future" | "too_old" {
  const t = Date.parse(occurredAt);
  if (t - now.getTime() > INGEST_LIMITS.futureSkewMs) return "too_far_in_future";
  if (!opts.backfill && now.getTime() - t > INGEST_LIMITS.maxAgeMs) return "too_old";
  return "ok";
}
